// Lesson 1: Aktivitas Pagi Hari - Question Configurations

export interface QuestionConfig {
    type: string;
    wordBank: number[]; // Word IDs to use
    questionSentence?: number[]; // Order of word IDs for sentence questions
    correctAnswer?: number[] | number; // Correct answer(s)
    options?: Array<{ id: number; text?: string }>; // Multiple choice options
    imagePrompt?: string; // For image-based questions
}

export interface MatchingPair {
    indonesian: string;
    batak: string;
}

// 1. Vocabulary Introduction Config (Image + Word)
export const unit5Lesson1VocabIntroConfigs = [
    {
        type: "vocab-intro",
        introWordId: 501, // "mangalak" (Bangun)
        imagePrompt: "morning_wake_up"
    },
    {
        type: "vocab-intro",
        introWordId: 502, // "manigos" (Mandi)
        imagePrompt: "person_bathing"
    },
    {
        type: "vocab-intro",
        introWordId: 503, // "mamangan parnggolu" (Sarapan)
        imagePrompt: "breakfast_table"
    },
    {
        type: "vocab-intro",
        introWordId: 504, // "manampuna" (Berdoa)
        imagePrompt: "person_praying"
    },
    {
        type: "vocab-intro",
        introWordId: 505, // "manogos" (Siap-siap)
        imagePrompt: "getting_ready"
    },
    {
        type: "vocab-intro",
        introWordId: 506, // "jam" (jam)
        imagePrompt: "clock"
    },
    {
        type: "vocab-intro",
        introWordId: 507, // "sude" (sudah)
        imagePrompt: "checkmark"
    },
    {
        type: "vocab-intro",
        introWordId: 508, // "onom" (enam)
        imagePrompt: "number_six"
    },
    {
        type: "vocab-intro",
        introWordId: 509, // "parnggolu" (pagi)
        imagePrompt: "sunrise"
    }
];

// 2. Vocabulary Quiz (Image → Choose correct word)
export const unit5Lesson1WordTranslationConfigs: QuestionConfig[] = [
    {
        type: "word-translation",
        wordBank: [501],
        imagePrompt: "morning_wake_up",
        correctAnswer: 501, // "mangalak"
        options: [
            { id: 501 }, // mangalak
            { id: 502 }, // manigos  
            { id: 503 }, // mamangan parnggolu
            { id: 504 }  // manampuna
        ]
    },
    {
        type: "word-translation",
        wordBank: [502],
        imagePrompt: "person_bathing",
        correctAnswer: 502, // "manigos"
        options: [
            { id: 501 }, // mangalak
            { id: 502 }, // manigos
            { id: 503 }, // mamangan parnggolu
            { id: 505 }  // manogos
        ]
    },
    {
        type: "word-translation",
        wordBank: [503],
        imagePrompt: "breakfast_table",
        correctAnswer: 503, // "mamangan parnggolu"
        options: [
            { id: 502 }, // manigos
            { id: 503 }, // mamangan parnggolu
            { id: 504 }, // manampuna
            { id: 505 }  // manogos
        ]
    },
    {
        type: "word-translation",
        wordBank: [504],
        imagePrompt: "person_praying",
        correctAnswer: 504, // "manampuna"
        options: [
            { id: 501 }, // mangalak
            { id: 503 }, // mamangan parnggolu
            { id: 504 }, // manampuna
            { id: 505 }  // manogos
        ]
    },
    {
        type: "word-translation",
        wordBank: [505],
        imagePrompt: "getting_ready",
        correctAnswer: 505, // "manogos"
        options: [
            { id: 501 }, // mangalak
            { id: 502 }, // manigos
            { id: 504 }, // manampuna
            { id: 505 }  // manogos
        ]
    }
];

// 3. Fill in the Blank Questions
export const unit5Lesson1FillInBlankConfigs: QuestionConfig[] = [
    {
        type: "fill-in-the-blank",
        wordBank: [105, 501, 506, 508, 509, 999],
        questionSentence: [105, 999, 506, 508, 509], // "Au _____ jam onom parnggolu"
        correctAnswer: 501, // "mangalak"
        options: [
            { id: 501 }, // mangalak
            { id: 502 }, // manigos
            { id: 503 }, // mamangan parnggolu
            { id: 504 }  // manampuna
        ]
    },
    {
        type: "fill-in-the-blank",
        wordBank: [107, 502, 507, 509, 999],
        questionSentence: [107, 999, 509], // "Ia _____ sude parnggolu"
        correctAnswer: 502, // "manigos"
        options: [
            { id: 501 }, // mangalak
            { id: 502 }, // manigos
            { id: 504 }, // manampuna
            { id: 505 }  // manogos
        ]
    },
    {
        type: "fill-in-the-blank",
        wordBank: [105, 503, 504, 507, 999],
        questionSentence: [105, 999, 507, 503], // "Au _____ sude mamangan parnggolu"
        correctAnswer: 504, // "manampuna"
        options: [
            { id: 502 }, // manigos
            { id: 503 }, // mamangan parnggolu
            { id: 504 }, // manampuna
            { id: 505 }  // manogos
        ]
    },
    {
        type: "fill-in-the-blank",
        wordBank: [107, 505, 506, 508, 999],
        questionSentence: [107, 999, 506, 508], // "Ia _____ jam onom"
        correctAnswer: 505, // "manogos"
        options: [
            { id: 501 }, // mangalak
            { id: 502 }, // manigos
            { id: 504 }, // manampuna
            { id: 505 }  // manogos
        ]
    },
    {
        type: "fill-in-the-blank",
        wordBank: [105, 503, 507, 509, 999],
        questionSentence: [105, 507, 999, 509], // "Au sude _____ parnggolu"
        correctAnswer: 503, // "mamangan parnggolu"
        options: [
            { id: 501 }, // mangalak
            { id: 502 }, // manigos
            { id: 503 }, // mamangan parnggolu
            { id: 505 }  // manogos
        ]
    }
];

// 4. Compose Sentence Questions
export const unit5Lesson1ComposeSentenceConfigs: QuestionConfig[] = [
    {
        type: "compose-sentences",
        wordBank: [105, 501, 506, 508, 509],
        questionSentence: [105, 501, 506, 508, 509], // "Au mangalak jam onom parnggolu."
        correctAnswer: [105, 501, 506, 508, 509],
        imagePrompt: "morning_routine"
    },
    {
        type: "compose-sentences",
        wordBank: [107, 502, 507, 509],
        questionSentence: [107, 502, 507, 509], // "Ia manigos sude parnggolu."
        correctAnswer: [107, 502, 507, 509],
        imagePrompt: "bathing_morning"
    },
    {
        type: "compose-sentences",
        wordBank: [105, 504, 503],
        questionSentence: [105, 504, 503], // "Au manampuna mamangan parnggolu."
        correctAnswer: [105, 504, 503],
        imagePrompt: "prayer_breakfast"
    },
    {
        type: "compose-sentences",
        wordBank: [107, 505, 506, 508],
        questionSentence: [107, 505, 506, 508], // "Ia manogos jam onom."
        correctAnswer: [107, 505, 506, 508],
        imagePrompt: "getting_ready_six"
    },
    {
        type: "compose-sentences",
        wordBank: [105, 507, 503, 509],
        questionSentence: [105, 507, 503, 509], // "Au sude mamangan parnggolu parnggolu."
        correctAnswer: [105, 507, 503, 509],
        imagePrompt: "finished_breakfast"
    }
];


export const unit5Lesson1WordMatchingPairs: MatchingPair[] = [
    { indonesian: "Bangun", batak: "ᯔᯰᯎᯝᯃ᯲" }, // Horas
    { indonesian: "Mandi", batak: "ᯔᯉᯪᯎᯬᯘ" }, // Anakkon
    { indonesian: "Sarapan", batak: "ᯔᯔᯰᯎᯀᯉ᯲ ᯇᯀᯒᯎᯬᯞᯮ" }, // Au
    { indonesian: "Berdoa", batak: "ᯔᯉᯔ᯲ᯇᯮᯉ" }, // Ho
    { indonesian: "Jam", batak: "ᯐᯔ᯲" }, // Ia
    { indonesian: "Pagi", batak: "ᯇᯀᯒᯎᯬᯞᯮ" }, // Saha
];

export const unit5Lesson1MatchingWordsConfig = {
    type: "matching-words",
    pairs: unit5Lesson1WordMatchingPairs,
};
